import { uploadChunked, appendUploadRef } from "../../../lib/upload";

const PLANNING_TITLE = "Planning 全套 (PDF/CAD)";
const BUILDING_TITLE = "Building Set (PDF/CAD)";

export const PB_DRAWINGS_TITLE = "P+B drawings";

export const mergePbDrawingRequirements = (requirements, mode, stageKey) => {
  if (mode !== "concurrent" || stageKey !== "pb_set") return requirements;
  const planning = requirements.find((r) => r.title === PLANNING_TITLE);
  const building = requirements.find((r) => r.title === BUILDING_TITLE);
  if (!planning?.taskId || !building?.taskId) return requirements;
  const merged = {
    ...planning,
    title: PB_DRAWINGS_TITLE,
    taskIds: [planning.taskId, building.taskId],
    discKeys: ["planning", "building"],
    pbDrawings: true,
  };
  return requirements.flatMap((r) => {
    if (r.taskId === planning.taskId) return [merged];
    if (r.taskId === building.taskId) return [];
    return [r];
  });
};

export const pbFileKind = (name) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (["dwg", "dxf"].includes(ext)) return "cad";
  return "other";
};

export const pbUploadTargets = (name, tasks) => {
  const kind = pbFileKind(name);
  return (tasks || []).flatMap((task) => {
    const types = task?.requiredFileTypes || [];
    const type = kind === "pdf" ? types.find((x) => /pdf/i.test(x))
      : kind === "cad" ? types.find((x) => /cad/i.test(x))
        : null;
    return task && type ? [{ task, type }] : [];
  });
};

export const uploadPbDrawings = async (api, files, tasks) => {
  const results = [];
  for (const file of Array.from(files || [])) {
    const targets = pbUploadTargets(file.name, tasks);
    if (!targets.length) throw new Error("P+B drawings 仅接受 PDF、DWG、DXF 图纸");
    const upload = await uploadChunked(api, file, { shrink: false });
    for (const { task, type } of targets) {
      const form = new FormData();
      appendUploadRef(form, upload, file);
      form.append("type", type);
      form.append("status", "Draft");
      const { data } = await api.post(`/design/tasks/${task.id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      results.push(data);
    }
  }
  return results;
};

export const pbMissingKinds = (tasks) => {
  const missing = new Set();
  (tasks || []).forEach((task) => {
    const files = task?.files || [];
    (task?.requiredFileTypes || []).forEach((type) => {
      if (files.some((f) => f.type === type && !f.voided && f.fileId)) return;
      if (/pdf/i.test(type)) missing.add("PDF");
      else if (/cad/i.test(type)) missing.add("CAD");
    });
  });
  return ["PDF", "CAD"].filter((kind) => missing.has(kind));
};

/* 同一 SHA 的跨任务副本算一份提交；同一任务重复上传仍保留两个版本。 */
export const mergePbCopies = (items, taskIdOf, shaOf) => {
  const merged = [];
  (items || []).forEach((item) => {
    const sha = shaOf(item);
    const taskId = taskIdOf(item);
    const match = sha && merged.find((entry) => entry.sha === sha && !entry.copies.some((copy) => taskIdOf(copy) === taskId));
    if (match) match.copies.push(item);
    else merged.push({ sha, copies: [item] });
  });
  return merged;
};

export const pbTaskFiles = (tasks) => {
  const items = (tasks || []).flatMap((task) => (task?.files || []).map((file) => ({ ...file, taskId: task.id })));
  return mergePbCopies(items, (file) => file.taskId, (file) => file.sha256 || file.fileId).map(({ copies }) => {
    const first = copies[0];
    const type = /cad/i.test(first.type || "") ? "P+B CAD" : /pdf/i.test(first.type || "") ? "P+B PDF" : first.type;
    return {
      ...first,
      type,
      locked: copies.some((file) => file.locked),
      pairRefs: copies.map((file) => ({ taskId: file.taskId, aid: file.aid })),
    };
  });
};

export const mergedPbStatus = (tasks) => {
  const statuses = (tasks || []).map((t) => t?.status || "Not Started");
  if (statuses.length && statuses.every((s) => ["Completed", "Approved"].includes(s))) return "Completed";
  if (statuses.some((s) => ["In Progress", "Internal Review", "Submitted", "Completed", "Approved"].includes(s))) return "In Progress";
  return statuses.includes("Ready") ? "Ready" : "Not Started";
};
